import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import {
    IFilterItem,
    FilterTreeNode,
    UseFilterLogicOptions,
    UseFilterLogicReturn,
    REPORT_FILTER_CONFIG,
    LAZY_FILTER_NAMES,
    SPECIAL_FILTER_PATHNAMES,
    PATHNAME_TO_REPORT_TYPE,
    ISelectedFilter,
} from '../components/reports/params/filter/types';
import {
    generateListKeys,
    getParentKey,
    prepareData,
} from '../../../helpers/utils/reports';
import {
    data as filtersData,
    updateFilters,
} from '../constants/filters';
import { useActions } from './useActions';
import { useTypedSelector } from './useTypedSelector';
import { useLoadGroupingsData } from './useLoadGroupingsData';
import { useUtmSections } from './useUtmSections';
import { injectUtmSectionsIntoFilters } from '../../../helpers/utils/utmSections';

/**
 * Хук для управления логикой фильтров
 * Объединяет логику из UniversalFilter и DashboardFilter
 * 
 * Удалён forceUpdate, добавлена мемоизация
 */
export const useFilterLogic = (options: UseFilterLogicOptions): UseFilterLogicReturn => {
    const {
        reportType: propReportType,
        filtersFilter: customFiltersFilter,
        disableAttr = false,
        isDashboard = false,
        isFixed = false,
        onActivateRequest,
    } = options;

    const location = useLocation();
    const prevPathRef = useRef(location.pathname);

    // Определяем тип отчёта из pathname если не передан
    const reportType = useMemo(() => {
        if (propReportType) return propReportType;
        return PATHNAME_TO_REPORT_TYPE[location.pathname];
    }, [propReportType, location.pathname]);

    // Redux state
    const { filters, filterParent } = useTypedSelector((state) => state.filter);
    const { currentDashboard } = useTypedSelector((state) => state.reports);

    // Redux actions
    const {
        setFilters,
        setFilterParent,
        setCurrentSegment,
        setCurrentFunnel,
    } = useActions();

    // Local state
    const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
    const [searchValue, setSearchValue] = useState('');
    const [autoExpandParent, setAutoExpandParent] = useState(true);
    const [rawData, setRawData] = useState<IFilterItem[]>(filtersData as IFilterItem[]);
    const [filterVisible, setFilterVisible] = useState(false);

    // Lazy loading hook
    const { onLoadData } = useLoadGroupingsData(setRawData as React.Dispatch<React.SetStateAction<unknown[]>>);

    // Секции разобранных UTM-меток регистрации — динамическая часть дерева фильтров
    const utmSections = useUtmSections();

    // Мемоизированные категории фильтров
    const filterCategories = useMemo((): string[] => {
        if (customFiltersFilter && customFiltersFilter.length > 0) {
            return customFiltersFilter;
        }
        if (reportType && REPORT_FILTER_CONFIG[reportType]) {
            return REPORT_FILTER_CONFIG[reportType].categories;
        }
        return [];
    }, [customFiltersFilter, reportType]);

    // Мемоизированные данные фильтров с учётом pathname
    const filterData = useMemo((): IFilterItem[] => {
        let data = rawData;

        // Для AD отчёта добавляем специальные фильтры
        if (location.pathname === SPECIAL_FILTER_PATHNAMES.AD_REPORTS || reportType === 'ad') {
            data = updateFilters() as IFilterItem[];
        }

        // Для webinar-quality исключаем период
        if (reportType === 'webinar-quality' || location.pathname === SPECIAL_FILTER_PATHNAMES.WEBINAR_QUALITY) {
            data = (updateFilters() as IFilterItem[]).map((filter) => ({
                ...filter,
                children: filter.children?.filter(
                    (child) => !child.name.startsWith('Период')
                ),
            }));
        }

        // Достраиваем секции разобранных UTM-меток (после узла «UTM - метка регистрации»)
        return injectUtmSectionsIntoFilters(data, utmSections) as IFilterItem[];
    }, [rawData, location.pathname, reportType, utmSections]);

    // Мемоизированная фильтрация по категориям
    const filteredData = useMemo(() => {
        return filterCategories.length > 0
            ? filterData.filter((item) => filterCategories.includes(item.name))
            : filterData;
    }, [filterData, filterCategories]);

    // Применяем флаги lazy-загрузки (мемоизированная функция)
    const applyLazyFlags = useCallback((nodes: IFilterItem[]): FilterTreeNode[] => {
        if (!Array.isArray(nodes)) return nodes as FilterTreeNode[];

        return nodes.map((node) => {
            const hasChildren = Array.isArray(node.children) && node.children.length > 0;
            const isLazyParent = LAZY_FILTER_NAMES.has(node.name);

            return {
                ...node,
                isLeaf: !isLazyParent && !hasChildren,
                children: hasChildren ? applyLazyFlags(node.children!) : node.children,
            } as FilterTreeNode;
        });
    }, []);

    // Мемоизированные данные дерева
    const treeData = useMemo((): FilterTreeNode[] => {
        let preparedData = prepareData(
            filteredData,
            searchValue,
            filters as ISelectedFilter[],
            disableAttr,
            '',
            '',
            '',
            '',
            [''],
            '',
            filterParent,
            onActivateRequest
        );

        return applyLazyFlags(preparedData);
    }, [filteredData, searchValue, filters, filterParent, disableAttr, onActivateRequest, applyLazyFlags]);

    // Автораскрытие при одной категории
    useEffect(() => {
        if (treeData.length === 1 && expandedKeys.length === 0 && treeData[0]?.key) {
            setAutoExpandParent(true);
            setExpandedKeys([treeData[0].key]);
        }
    }, [treeData.length, expandedKeys.length]);

    // Сброс при смене пути
    useEffect(() => {
        const prev = prevPathRef.current;
        const next = location.pathname;

        if (prev !== next && !isFixed) {
            setFilters([]);
            setFilterParent([]);
            setSearchValue('');
            setExpandedKeys([]);
            setAutoExpandParent(false);
            setFilterVisible(false);
        }

        prevPathRef.current = next;
    }, [location.pathname, setFilters, setFilterParent, isFixed]);

    // Сброс при очистке поиска
    useEffect(() => {
        if (!searchValue) {
            setExpandedKeys([]);
            setAutoExpandParent(false);
        }
    }, [searchValue]);

    // Мемоизированный listKeys для поиска
    // Ключи поиска считаем с учётом секций, иначе поиск не находит их пункты
    const listKeys = useMemo(
        () => generateListKeys(injectUtmSectionsIntoFilters(filtersData, utmSections)),
        [ utmSections ]
    );

    // Обработчик раскрытия узлов
    const onExpand = useCallback((keys: string[]) => {
        setExpandedKeys(keys);
        setAutoExpandParent(false);
    }, []);

    // Обработчик поиска
    const onSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;

        if (value !== '') {
            const reg = new RegExp(value, 'i');

            const newExpandedKeys = listKeys
                .map((item: { name: string; id: string }) => {
                    if (item.name.search(reg) > -1) {
                        return getParentKey(item.id, treeData);
                    }
                    return null;
                })
                .filter(
                    (item: string | null, i: number, self: (string | null)[]) =>
                        item && self.indexOf(item) === i
                ) as string[];

            setExpandedKeys(newExpandedKeys);
            setSearchValue(value);
            setAutoExpandParent(true);
        } else {
            setExpandedKeys([]);
            setSearchValue('');
            setAutoExpandParent(false);
        }
    }, [listKeys, treeData]);

    // Очистка фильтров
    const onClearFilters = useCallback(() => {
        setCurrentSegment(null);
        setCurrentFunnel([]);
        setFilters([]);
        setFilterParent([]);
    }, [setCurrentSegment, setCurrentFunnel, setFilters, setFilterParent]);

    // Обёртка для onLoadData
    const handleLoadData = useCallback(async (node: FilterTreeNode): Promise<void> => {
        await onLoadData(node as unknown as { key: string; children?: unknown[] });
    }, [onLoadData]);

    return {
        treeData,
        expandedKeys,
        searchValue,
        autoExpandParent,
        filterVisible,
        rawData,
        setRawData,
        setExpandedKeys,
        setFilterVisible,
        onExpand,
        onSearch,
        onClearFilters,
        onLoadData: handleLoadData,
    };
};

export default useFilterLogic;
